import { sampleAirTemperature, sampleWorldCalendar, type WeatherPreset, type WorldEnvironmentState } from "@endclock/shared";

const cloud = '<path d="M7 20h18a5 5 0 0 0 0-10 8 8 0 0 0-15-1 5.6 5.6 0 0 0-3 11Z" fill="#afbfca" stroke="#d1dbe0"/>';
const sun = '<g stroke="#ebc86f" stroke-width="2"><circle cx="12" cy="11" r="5" fill="#dfb557"/><path d="M12 1v2m0 16v2M2 11H0m24 0h-2M4 3l2 2m12 12 2 2M4 19l2-2M18 5l2-2"/></g>';
const rain = '<path d="m10 24-2 4m10-4-2 4m10-4-2 4" stroke="#77b2dd" stroke-width="2"/>';
const snow = '<path d="M10 22v9m-4-7 8 5m0-5-8 5m19-7v9m-4-7 8 5m0-5-8 5" stroke="#c5e5ed" stroke-width="1.5"/>';

/** Original static paths; no assets, emoji, or icon animation loop. */
export function weatherIcon(preset: WeatherPreset): string {
  const artwork: Record<WeatherPreset, string> = {
    CLEAR: `<g transform="translate(5 4)">${sun}</g>`,
    PARTLY_CLOUDY: sun + cloud,
    OVERCAST: cloud,
    FOG: cloud + '<path d="M3 24h26M6 28h20" stroke="#a8b8af" stroke-width="2"/>',
    WINDY: '<path d="M2 10h21c8 0 7-8 2-7M4 16h22M2 22h18c8 0 7 8 2 7" fill="none" stroke="#aad0be" stroke-width="2"/>',
    DRIZZLE: cloud + '<path d="M11 25v2m10-2v2" stroke="#77b2dd" stroke-width="2"/>',
    RAIN: cloud + rain,
    HEAVY_RAIN: cloud + rain + '<path d="m5 22-2 4m30-4-2 4m-16 5-1 3" stroke="#77b2dd" stroke-width="2"/>',
    THUNDERSTORM: cloud + '<path d="m18 15-7 10h6l-2 7 10-13h-7l3-4" fill="#efd16a"/>',
    SNOW: cloud + snow,
    HEAVY_SNOW: cloud + snow + '<path d="M18 21v9m-3-7 6 4m0-4-6 4" stroke="#edf8fa" stroke-width="1.5"/>',
    HAIL: cloud + '<g fill="#aed5e3"><circle cx="8" cy="26" r="2"/><circle cx="17" cy="29" r="2"/><circle cx="26" cy="25" r="2"/></g>',
  };
  const label = preset.replaceAll("_", " ");
  return `<svg viewBox="0 0 36 34" role="img" aria-label="${label}"><title>${label}</title>${artwork[preset]}</svg>`;
}

export function environmentReadout(state: WorldEnvironmentState, at: number) {
  const calendar = sampleWorldCalendar(state, at);
  const minutes = Math.floor(calendar.worldTimeMinutes);
  return {
    day: `DAY ${calendar.dayOfYear}`,
    clock: `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`,
    temperature: `${Math.round(sampleAirTemperature(state, at))}°C`,
    preset: state.weatherPreset,
  };
}

export function setupEnvironmentHud(sample: () => { state: WorldEnvironmentState; at: number }): () => void {
  const root = document.querySelector<HTMLElement>("#environment-hud");
  if (!root) return () => {};
  let previous = "";
  const render = () => {
    if (document.hidden) return;
    const { state, at } = sample();
    const value = environmentReadout(state, at);
    const key = `${value.day}|${value.clock}|${value.temperature}|${value.preset}`;
    if (key === previous) return;
    previous = key;
    root.innerHTML = `<span data-environment="day">${value.day}</span><time>${value.clock}</time><span data-environment="temperature">${value.temperature}</span><span class="weather-icon">${weatherIcon(value.preset)}</span>`;
    root.setAttribute("aria-label", `${value.day}, ${value.clock}, ${value.temperature}, ${value.preset.replaceAll("_", " ")}`);
  };
  render();
  const timer = window.setInterval(render, 1_000);
  return () => window.clearInterval(timer);
}
